// The weather now for Home's greeting bar and its notifications (a weather warning). From VERA's server (Azure Maps),
// which caches it for 10 minutes per place; here it is kept for 10 minutes too, so Home and its cards share one answer.

import { useEffect, useState } from "react";
import { auth } from "../auth/auth";
import { cpFetch } from "./controlPlane";
import { currentLocation, locationOn } from "./location";
import type { HomePlace } from "./homePrefs";

export type WeatherNow = {
  phrase: string; icon: number; temperature: number; unit: "F" | "C"; windSpeed: number; windGust?: number | null; speedUnit: string;
  windFrom?: number | null; windFromText?: string | null; rainChance?: number | null; next?: string | null;
};

/** US English reads °F and mph; everyone else °C and km/h. */
export const metricHere = () => { try { return !/^en-US$/i.test(navigator.language); } catch { return true; } };

let cache: { at: number; key: string; value: WeatherNow | null } | null = null;

export async function weatherFor(place: HomePlace | null | undefined): Promise<{ weather: WeatherNow | null; where: string | null; reason?: string }> {
  let where = place ?? null;
  if (!where && locationOn()) { const here = await currentLocation(); if (here) where = { name: "", lat: here.lat, lon: here.lon }; }
  if (!where) return { weather: null, where: null, reason: "noplace" };
  const metric = metricHere();
  const k = `${where.lat.toFixed(2)},${where.lon.toFixed(2)},${metric}`;
  if (cache && cache.key === k && Date.now() - cache.at < 10 * 60_000) return { weather: cache.value, where: where.name || null };
  try {
    const value = await cpFetch<WeatherNow>(() => auth.getToken(), `/v1/weather/now?lat=${where.lat}&lon=${where.lon}&units=${metric ? "metric" : "imperial"}`);
    cache = { at: Date.now(), key: k, value };
    return { weather: value, where: where.name || null };
  } catch { return { weather: null, where: where.name || null, reason: "error" }; }
}

export const findPlace = (q: string) => cpFetch<HomePlace>(() => auth.getToken(), `/v1/weather/place?q=${encodeURIComponent(q)}`);

/** Strong wind or likely rain or storms: worth a notification for anyone working on the water. */
export function weatherWarning(w: WeatherNow | null): string | null {
  if (!w) return null;
  const strong = w.speedUnit === "mph" ? 25 : 40;
  const gust = w.windGust ?? w.windSpeed;
  if (gust >= strong) return `Strong wind: gusts ${Math.round(gust)} ${w.speedUnit}${w.windFromText ? ` from the ${w.windFromText}` : ""}.`;
  if (/thunder|storm/i.test(`${w.phrase} ${w.next ?? ""}`)) return `Storms expected${w.next ? `: ${w.next}` : ""}.`;
  if ((w.rainChance ?? 0) >= 60) return `Rain likely in the next hours (${w.rainChance}%).`;
  return null;
}

export function useWeather(place: HomePlace | null | undefined, on: boolean) {
  const [state, setState] = useState<{ weather: WeatherNow | null; where: string | null; reason?: string } | null>(null);
  const k = place ? `${place.lat},${place.lon}` : "here";
  useEffect(() => {
    if (!on) { setState(null); return; }
    let live = true;
    const load = () => void weatherFor(place).then((r) => { if (live) setState(r); });
    load();
    const t = window.setInterval(load, 10 * 60_000);
    return () => { live = false; window.clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k, on]);
  return state;
}
